import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

import config from "@/app/config.js";
import theme from "@/lib/constants/theme.js";

const THEME_NAMES = {
  LIGHT: "sensity_light",
  DARK: "sensity_dark",
  CUSTOM: "custom",
};

const PREFERENCE_VALUES = {
  LIGHT: "light",
  DARK: "dark",
  AUTO: "auto",
};

// daisyUI 4.x color name -> CSS variable mapping
const COLOR_VAR_MAP = {
  primary: "--p",
  "primary-content": "--pc",
  secondary: "--s",
  "secondary-content": "--sc",
  accent: "--a",
  "accent-content": "--ac",
  neutral: "--n",
  "neutral-content": "--nc",
  "base-100": "--b1",
  "base-200": "--b2",
  "base-300": "--b3",
  "base-content": "--bc",
  info: "--in",
  "info-content": "--inc",
  success: "--su",
  "success-content": "--suc",
  warning: "--wa",
  "warning-content": "--wac",
  error: "--er",
  "error-content": "--erc",
};

// module-level ref for on-prem theme data (shared across useTheme() calls)
const onPremThemeData = ref(null);

async function fetchOnPremTheme() {
  try {
    const response = await fetch("/theme.json");
    if (!response.ok) return null;
    return await response.json();
  } catch {
    // theme.json not found or invalid - gracefully fall back to defaults
    return null;
  }
}

function applyOnPremColors(colors) {
  const root = document.documentElement;
  for (const [colorName, oklchValue] of Object.entries(colors)) {
    const cssVar = COLOR_VAR_MAP[colorName];
    if (cssVar && oklchValue) {
      root.style.setProperty(cssVar, oklchValue);
    }
  }
}

export function useTheme() {
  const store = useStore();

  const systemThemeMediaQuery = ref(null);
  const systemThemeChangeHandler = ref(null);

  const themePreference = computed(() => store.getters.getThemePreference);
  const currentTheme = computed(() => store.getters.getTheme);
  const isCustomTheme = computed(() => store.getters.isCustomTheme);
  const isDarkTheme = computed(() => currentTheme.value === THEME_NAMES.DARK);
  const hasOnPremLogo = computed(() => config.VITE_APP_IS_ONPREM === "on" && isCustomTheme.value);

  const themeOptions = [
    { value: PREFERENCE_VALUES.LIGHT, text: "Light", icon: "bi-sun" },
    { value: PREFERENCE_VALUES.DARK, text: "Dark", icon: "bi-moon" },
    { value: PREFERENCE_VALUES.AUTO, text: "Auto", icon: "bi-circle-half" },
  ];

  const selectedThemeOption = computed(() => {
    const preference = themePreference.value || PREFERENCE_VALUES.AUTO;

    return themeOptions.find((option) => option.value === preference) || themeOptions[2];
  });

  const selectedThemeIcon = computed(() => selectedThemeOption.value.icon);

  function getSystemTheme() {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return THEME_NAMES.DARK;
      }
    } catch {
      // Gracefully handle matchMedia errors in older browsers
    }

    return THEME_NAMES.LIGHT;
  }

  function applyTheme(themeName) {
    document.documentElement.setAttribute("data-theme", themeName);
    store.dispatch("setTheme", themeName);
  }

  function resolveThemeFromPreference(preference) {
    if (preference === PREFERENCE_VALUES.AUTO) {
      return getSystemTheme();
    }

    return preference === PREFERENCE_VALUES.LIGHT ? THEME_NAMES.LIGHT : THEME_NAMES.DARK;
  }

  async function initializeTheme() {
    applyNonProductionMetaTags();

    // on-prem runtime theme
    if (config.VITE_APP_IS_ONPREM === "on") {
      const themeData = await fetchOnPremTheme();
      onPremThemeData.value = themeData;

      if (themeData?.colors && Object.keys(themeData.colors).length > 0) {
        applyTheme(THEME_NAMES.CUSTOM);
        applyOnPremColors(themeData.colors);
        document.title = "End-To-End Deepfake Detection";
        updateFavicon("/custom-favicon.ico");
      } else {
        applyTheme(THEME_NAMES.LIGHT);
      }

      return;
    }

    // cloud/dev - existing static theme.js check
    if (theme.assets?.logo) {
      applyTheme(THEME_NAMES.CUSTOM);
      document.title = "End-To-End Deepfake Detection";
      updateFavicon("/custom-favicon.ico");

      return;
    }

    const preference = themePreference.value || PREFERENCE_VALUES.AUTO;
    const appliedTheme = resolveThemeFromPreference(preference);
    applyTheme(appliedTheme);
  }

  function setThemePreference(preference) {
    store.dispatch("setThemePreference", preference);

    if (config.VITE_APP_IS_ONPREM === "on" && onPremThemeData.value?.colors) {
      return;
    }

    if (theme.assets?.logo) {
      return;
    }

    const appliedTheme = resolveThemeFromPreference(preference);
    applyTheme(appliedTheme);
  }

  function handleSystemThemeChange(e) {
    if (config.VITE_APP_IS_ONPREM === "on" && onPremThemeData.value?.colors) {
      return;
    }

    if (theme.assets?.logo) {
      return;
    }

    const preference = themePreference.value || PREFERENCE_VALUES.AUTO;
    if (preference === PREFERENCE_VALUES.AUTO) {
      const newTheme = e.matches ? THEME_NAMES.DARK : THEME_NAMES.LIGHT;
      applyTheme(newTheme);
    }
  }

  function setupSystemThemeListener() {
    try {
      if (!window.matchMedia) {
        return;
      }

      systemThemeMediaQuery.value = window.matchMedia("(prefers-color-scheme: dark)");

      if (systemThemeMediaQuery.value.addEventListener) {
        systemThemeMediaQuery.value.addEventListener("change", handleSystemThemeChange);
      } else if (systemThemeMediaQuery.value.addListener) {
        systemThemeMediaQuery.value.addListener(handleSystemThemeChange);
      }

      systemThemeChangeHandler.value = handleSystemThemeChange;
    } catch {
      // Gracefully handle matchMedia errors
    }
  }

  function cleanupSystemThemeListener() {
    if (!systemThemeMediaQuery.value || !systemThemeChangeHandler.value) {
      return;
    }

    try {
      if (systemThemeMediaQuery.value.removeEventListener) {
        systemThemeMediaQuery.value.removeEventListener("change", systemThemeChangeHandler.value);
      } else if (systemThemeMediaQuery.value.removeListener) {
        systemThemeMediaQuery.value.removeListener(systemThemeChangeHandler.value);
      }
    } catch {
      // Gracefully handle cleanup errors
    }
  }

  function applyNonProductionMetaTags() {
    if (config.VITE_APP_ALLOW_INDEXING === "true") return;

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    // remove branding meta tags that leak Sensity identity on non-production deployments
    document.querySelector('meta[name="description"]')?.remove();
    document.querySelector('meta[property="og:locale"]')?.remove();
    document.querySelector('meta[property="fb:app_id"]')?.remove();
  }

  function updateFavicon(href) {
    const existingFavicons = document.querySelectorAll("link[rel*='icon']");
    existingFavicons.forEach((e) => e.parentNode.removeChild(e));

    const link = document.createElement("link");
    link.rel = "shortcut icon";
    link.type = "image/x-icon";
    link.href = href;
    document.getElementsByTagName("head")[0].appendChild(link);
  }

  function useThemeLifecycle() {
    onMounted(async () => {
      await initializeTheme();
      setupSystemThemeListener();
    });

    onBeforeUnmount(() => {
      cleanupSystemThemeListener();
    });
  }

  return {
    themePreference,
    currentTheme,
    isCustomTheme,
    isDarkTheme,
    hasOnPremLogo,
    themeOptions,
    selectedThemeOption,
    selectedThemeIcon,
    setThemePreference,
    initializeTheme,
    useThemeLifecycle,
  };
}
